/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-return-assign */
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";

type KeysAnimationPropsType = {
  shouldAnimateAppearKeys: boolean;
};

function KeysAnimation({ shouldAnimateAppearKeys }: KeysAnimationPropsType) {
  const keyRef = useRef<(HTMLElement | null)[]>([]);
  const apearKeyRef = useRef<(HTMLElement | null)[]>([]);

  const animateKeys = (
    stick: gsap.TweenTarget,
    end: gsap.TweenVars,
    rotateEnd: number,
    duration: number,
    ease = "power3.out",
  ) => {
    gsap.to(stick, {
      ...end,
      opacity: 1,
      rotate: rotateEnd,
      duration,
      scale: 1,
      yoyo: true,
      ease,
    });
  };

  const animateKeysSlow = (duration: number) => {
    animateKeys(keyRef.current[0], { x: -600, y: 120 }, 670, duration);
    animateKeys(keyRef.current[1], { x: -400, y: -300 }, 720, duration);
    animateKeys(keyRef.current[2], { x: 280, y: -320 }, 730, duration);
    animateKeys(keyRef.current[3], { x: -250, y: 200 }, 710, duration);
    animateKeys(keyRef.current[4], { x: -100, y: -460 }, 760, duration);
    animateKeys(keyRef.current[5], { x: 700, y: 150 }, 730, duration);
    animateKeys(keyRef.current[6], { x: 550, y: -100 }, 760, duration);
    animateKeys(keyRef.current[7], { x: 450, y: 30 }, 700, duration);
    animateKeys(keyRef.current[8], { x: -450, y: -120 }, 760, duration);
    animateKeys(keyRef.current[9], { x: -380, y: 50 }, 760, duration);

    animateKeys(keyRef.current[10], { x: 140, y: -480 }, 795, duration);
    animateKeys(keyRef.current[11], { x: -650, y: -200 }, 740, duration);
    animateKeys(keyRef.current[12], { x: 400, y: -200 }, 740, duration);
    animateKeys(keyRef.current[13], { x: 550, y: -300 }, 670, duration);
    animateKeys(keyRef.current[14], { x: 750, y: -50 }, 660, duration);
  };

  const fallingKeys = (elements: any[]) => {
    elements.forEach((element: gsap.TweenTarget, index: number) => {
      gsap.to(element, {
        opacity: 0,
        y: "+=250",
        rotate: 320,
        duration: 2,
        ease: "power1.inOut",
        delay: index * 0.1,
      });
    });
  };

  const appearElements = (elements: any[]) => {
    elements.forEach((element: gsap.TweenTarget, index: number) => {
      gsap.to(element, {
        opacity: 1,
        duration: 2,
        delay: index * 0.1,
        ease: "bounce.out",
      });
    });
  };

  useEffect(() => {
    let timer2: NodeJS.Timeout | undefined;
    let timer3: NodeJS.Timeout | undefined;

    const timer = setTimeout(() => {
      animateKeysSlow(2);

      if (!shouldAnimateAppearKeys) {
        timer2 = setTimeout(() => {
          fallingKeys([...keyRef.current]);
        }, 2000);

        timer3 = setTimeout(() => {
          appearElements([...apearKeyRef.current]);
        }, 3000);
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (timer2) clearTimeout(timer2);
      if (timer3) clearTimeout(timer3);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='absolute left-1/2 bottom-[130px] w-[100px] -translate-x-1/2 text-small font-medium text-txt-primary'>
      <div
        ref={(el) => (apearKeyRef.current[0] = el)}
        className='animate-key right-[191px] top-[13px] h-[43px] w-7 scale-[0.4] border-green-dark bg-green-light'
      >
        Ã
      </div>
      <div
        ref={(el) => (apearKeyRef.current[1] = el)}
        className='animate-key right-[154px] -top-[3px] h-[43px] w-7 scale-[0.4] border-indigo-dark bg-indigo-light'
      >
        N
      </div>
      <div
        ref={(el) => (apearKeyRef.current[2] = el)}
        className='animate-key right-[199px] -top-[24px] h-[43px] w-7 scale-[0.8] border-pink-dark bg-pink-light'
      >
        Ş
      </div>
      <div
        ref={(el) => (apearKeyRef.current[3] = el)}
        className='animate-key right-[172px] -top-[23px] h-[43px] w-7 scale-[0.4] border-mint-dark bg-mint-light'
      >
        É
      </div>
      <div
        ref={(el) => (apearKeyRef.current[4] = el)}
        className='animate-key right-[138px] -top-[30px] h-[43px] w-7 scale-[0.4] border-yellow-dark bg-yellow-light'
      >
        Я
      </div>
      <div
        ref={(el) => (apearKeyRef.current[5] = el)}
        className='animate-key right-[225px] -top-[42px] h-[43px] w-7 scale-[0.4] border-green-dark bg-green-light'
      >
        Ğ
      </div>
      <div
        ref={(el) => (apearKeyRef.current[6] = el)}
        className='animate-key right-[191px] bottom-[15px] h-[43px] w-7 scale-[0.4] border-green-dark bg-green-light'
      >
        Ö
      </div>
      <div
        ref={(el) => (apearKeyRef.current[7] = el)}
        className='animate-key right-[165px] bottom-[19px] h-[43px] w-7 scale-[0.8] border-indigo-dark bg-indigo-light'
      >
        Ł
      </div>
      <div
        ref={(el) => (apearKeyRef.current[8] = el)}
        className='animate-key right-[138px] bottom-[17px] h-[43px] w-7 scale-[0.4] border-pink-dark bg-pink-light'
      >
        Ж
      </div>
      <div
        ref={(el) => (apearKeyRef.current[9] = el)}
        className='animate-key right-[218px] bottom-[32px] h-[43px] w-7 scale-[0.8] border-yellow-dark bg-yellow-light'
      >
        A
      </div>
      <div
        ref={(el) => (apearKeyRef.current[10] = el)}
        className='animate-key right-[191px] bottom-[42px] h-[43px] w-7 scale-[0.4] border-indigo-dark bg-indigo-light'
      >
        Ґ
      </div>
      <div
        ref={(el) => (apearKeyRef.current[11] = el)}
        className='animate-key right-[152px] bottom-[49px] h-[43px] w-7 scale-[0.4] border-green-dark bg-green-light'
      >
        Я
      </div>
      <div
        ref={(el) => (apearKeyRef.current[12] = el)}
        className='animate-key right-[171px] bottom-[62px] h-[43px] w-7 scale-[0.4] border-orange-dark bg-orange-light'
      >
        F
      </div>
      <div
        ref={(el) => (apearKeyRef.current[13] = el)}
        className='animate-key right-[211px] bottom-[68px] h-[43px] w-7 scale-[0.4] border-green-dark bg-green-light'
      >
        C
      </div>
      <div
        ref={(el) => (apearKeyRef.current[14] = el)}
        className='animate-key right-[152px] bottom-[86px] h-[43px] w-7 scale-[0.4] border-indigo-dark bg-indigo-light'
      >
        Ї
      </div>

      <div
        ref={(el) => (keyRef.current[0] = el)}
        className='animate-key h-[43px] w-7 border-green-dark bg-green-light will-change-transform'
      >
        Ã
      </div>
      <div
        ref={(el) => (keyRef.current[1] = el)}
        className='animate-key h-[43px] w-7 border-indigo-dark bg-indigo-light will-change-transform'
      >
        N
      </div>
      <div
        ref={(el) => (keyRef.current[2] = el)}
        className='animate-key h-[43px] w-7 border-pink-dark bg-pink-light will-change-transform'
      >
        Ş
      </div>
      <div
        ref={(el) => (keyRef.current[3] = el)}
        className='animate-key h-[43px] w-7 border-mint-dark bg-mint-light will-change-transform'
      >
        É
      </div>
      <div
        ref={(el) => (keyRef.current[4] = el)}
        className='animate-key h-[43px] w-7 border-yellow-dark bg-yellow-light will-change-transform'
      >
        Я
      </div>
      <div
        ref={(el) => (keyRef.current[5] = el)}
        className='animate-key h-[43px] w-7 border-green-dark bg-green-light will-change-transform'
      >
        Ğ
      </div>
      <div
        ref={(el) => (keyRef.current[6] = el)}
        className='animate-key h-[43px] w-7 border-green-dark bg-green-light will-change-transform'
      >
        Ö
      </div>
      <div
        ref={(el) => (keyRef.current[7] = el)}
        className='animate-key h-[43px] w-7 border-indigo-dark bg-indigo-light will-change-transform'
      >
        Ł
      </div>
      <div
        ref={(el) => (keyRef.current[8] = el)}
        className='animate-key h-[43px] w-7 border-pink-dark bg-pink-light will-change-transform'
      >
        Ж
      </div>
      <div
        ref={(el) => (keyRef.current[9] = el)}
        className='animate-key h-[43px] w-7 border-yellow-dark bg-yellow-light will-change-transform'
      >
        A
      </div>
      <div
        ref={(el) => (keyRef.current[10] = el)}
        className='animate-key h-[43px] w-7 border-indigo-dark bg-indigo-light will-change-transform'
      >
        Ґ
      </div>
      <div
        ref={(el) => (keyRef.current[11] = el)}
        className='animate-key h-[43px] w-7 border-green-dark bg-green-light will-change-transform'
      >
        Я
      </div>
      <div
        ref={(el) => (keyRef.current[12] = el)}
        className='animate-key h-[43px] w-7 border-orange-dark bg-orange-light will-change-transform'
      >
        F
      </div>
      <div
        ref={(el) => (keyRef.current[13] = el)}
        className='animate-key h-[43px] w-7 border-green-dark bg-green-light will-change-transform'
      >
        C
      </div>
      <div
        ref={(el) => (keyRef.current[14] = el)}
        className='animate-key h-[43px] w-7 border-indigo-dark bg-indigo-light will-change-transform'
      >
        Ї
      </div>
    </div>
  );
}

export default KeysAnimation;
